import { useEffect, useState } from "react";
import { fetchAdsFeedImportStatus, type AdsFeedImportStatusPayload } from "@/lib/adsAssistantClient";

type Props = {
  scopedOrgId: string | null;
  /** When true, show Meta/Google vault hints for trainer advanced import. */
  showVaultHints?: boolean;
};

export function AdsAssistantFeedImportStatusBanner({ scopedOrgId, showVaultHints }: Props) {
  const [status, setStatus] = useState<AdsFeedImportStatusPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!scopedOrgId) {
      setStatus(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void fetchAdsFeedImportStatus().then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        setStatus(null);
        return;
      }
      setError(null);
      setStatus(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [scopedOrgId]);

  if (!scopedOrgId || loading) {
    return loading ? (
      <p className="text-[11px] text-gray-500 dark:text-gray-400">Checking CRM connection…</p>
    ) : null;
  }

  if (error) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50/80 px-2.5 py-2 text-[11px] text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
        Could not load import status: {error}
      </p>
    );
  }

  if (!status) return null;

  const ghlReady =
    status.ghl.active_connections > 0 && status.ghl.connections.every((c) => c.has_api_key);
  const ghlPartial =
    status.ghl.active_connections > 0 && status.ghl.connections.some((c) => !c.has_api_key);

  const tone = ghlReady
    ? "border-emerald-200 bg-emerald-50/80 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100"
    : ghlPartial || status.ghl.active_connections > 0
      ? "border-amber-200 bg-amber-50/80 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100"
      : "border-amber-200 bg-amber-50/80 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100";

  const connectionLabel =
    status.ghl.active_connections === 0
      ? "No active CRM connection — add one in Admin → CRM."
      : status.ghl.needs_connection_id
        ? `${status.ghl.active_connections} CRM connections — pick connection when syncing if prompted.`
        : status.ghl.connections.length === 1
          ? `CRM connected${status.ghl.connections[0]?.company_name ? `: ${status.ghl.connections[0].company_name}` : ""}.`
          : `${status.ghl.active_connections} CRM connections active.`;

  const mappingLine = `${status.mappings.locations_with_facebook_page_id} of ${status.mappings.locations_total} locations have Facebook Page IDs; ${status.mappings.locations_with_google_business_location_id} have Google Business IDs. Configure in Org Settings → Social mappings.`;

  return (
    <div className={`rounded-lg border px-2.5 py-2 text-[11px] leading-snug ${tone}`} role="status">
      <p className="font-semibold">{ghlReady ? "CRM ready for sync" : "CRM connection"}</p>
      <p className="mt-0.5">{connectionLabel}</p>
      <p className="mt-1 opacity-90">{mappingLine}</p>
      {showVaultHints ? (
        <p className="mt-1 opacity-80">
          Meta token {status.meta.configured ? "saved" : "not saved"} · Google credentials{" "}
          {status.google.configured ? "saved" : "not saved"}
          {!status.meta.encryption_configured || !status.google.encryption_configured
            ? " (server encryption keys may be missing)"
            : ""}
        </p>
      ) : null}
    </div>
  );
}
